/**
 * Long-press tooltip helper for touch devices (B12).
 *
 * On fine-pointer devices (desktop mouse) this is a no-op — CSS :hover handles
 * tooltip visibility there.  On coarse-pointer devices (touch), a 500 ms hold
 * adds `--touch-open` to `host`; a tap outside or a finger move > 8 px clears it.
 *
 * Returns a cleanup function that removes all listeners and cancels pending timers.
 */

export interface LongPressOptions {
  /**
   * Class toggled on the host element when the tooltip opens (default: "--touch-open").
   * Ignored if `onOpen` / `onClose` callbacks are provided.
   */
  className?: string;
  /** How long the pointer must be held before the tooltip opens (default: 500 ms). */
  holdMs?: number;
  /** Maximum movement allowed before the hold is cancelled (default: 8 px). */
  moveThresholdPx?: number;
  /**
   * Optional callback invoked instead of toggling `className` when the long-press fires.
   * If provided, `onClose` must also be provided.
   */
  onOpen?: () => void;
  /**
   * Optional callback invoked instead of removing `className` when the tooltip should close.
   * If provided, `onOpen` must also be provided.
   */
  onClose?: () => void;
}

export function attachLongPressTooltip(
  host: HTMLElement,
  options?: LongPressOptions,
): () => void {
  // Only activate on coarse/touch devices; fine pointer (mouse) uses CSS :hover.
  if (!matchMedia("(hover: none) and (pointer: coarse)").matches) {
    return () => { /* no-op on desktop */ };
  }

  const className = options?.className ?? "--touch-open";
  const holdMs = options?.holdMs ?? 500;
  const moveThresholdPx = options?.moveThresholdPx ?? 8;
  const onOpen = options?.onOpen;
  const onClose = options?.onClose;
  // Use callbacks if both are provided, otherwise use CSS class.
  const useCallbacks = typeof onOpen === "function" && typeof onClose === "function";

  let timerId: ReturnType<typeof setTimeout> | null = null;
  let startX = 0;
  let startY = 0;

  // One-shot outside-tap listener (attached when tooltip opens).
  let outsideListener: ((e: PointerEvent) => void) | null = null;

  function cancelTimer(): void {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  function closeTooltip(): void {
    if (useCallbacks) {
      onClose!();
    } else {
      host.classList.remove(className);
    }
    if (outsideListener) {
      window.removeEventListener("pointerdown", outsideListener, true);
      outsideListener = null;
    }
  }

  function openTooltip(): void {
    if (useCallbacks) {
      onOpen!();
    } else {
      host.classList.add(className);
    }

    // One-shot: next tap outside host closes the tooltip.
    outsideListener = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node) || !host.contains(t)) {
        closeTooltip();
      }
    };
    // Use capture so we intercept before other handlers; register after current event loop.
    setTimeout(() => {
      if (outsideListener) {
        window.addEventListener("pointerdown", outsideListener, { capture: true, once: true });
      }
    }, 0);
  }

  function onPointerDown(e: PointerEvent): void {
    if (e.button !== 0) return; // primary button only
    cancelTimer();
    startX = e.clientX;
    startY = e.clientY;
    timerId = setTimeout(() => {
      timerId = null;
      openTooltip();
    }, holdMs);
  }

  function onPointerMove(e: PointerEvent): void {
    if (timerId === null) return;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;
    if (Math.hypot(dx, dy) > moveThresholdPx) {
      cancelTimer();
    }
  }

  function onPointerUpOrCancel(): void {
    cancelTimer();
  }

  host.addEventListener("pointerdown", onPointerDown);
  host.addEventListener("pointermove", onPointerMove);
  host.addEventListener("pointerup", onPointerUpOrCancel);
  host.addEventListener("pointercancel", onPointerUpOrCancel);
  host.addEventListener("pointerleave", onPointerUpOrCancel);

  return function cleanup(): void {
    cancelTimer();
    closeTooltip();
    host.removeEventListener("pointerdown", onPointerDown);
    host.removeEventListener("pointermove", onPointerMove);
    host.removeEventListener("pointerup", onPointerUpOrCancel);
    host.removeEventListener("pointercancel", onPointerUpOrCancel);
    host.removeEventListener("pointerleave", onPointerUpOrCancel);
  };
}
